import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createScalingSetting,
  deleteScalingSetting,
  getScalingSettings,
  updateScalingSetting,
  ScalingCreatePayload,
  ScalingUpdatePayload,
} from '@/api/scalingService';
import { queryKeys } from '@/lib/queryKeys';
import { toaster } from '@/components/ui/toaster';

const errorMessage = (error: unknown) => {
  const res = (error as { response?: { data?: { message?: string } } })?.response;
  if (res?.data?.message) return res.data.message;
  return error instanceof Error ? error.message : 'Une erreur est survenue';
};

const PAGE_SIZE = 20;

export const useScaling = (filters: { theme?: string; sport?: string; format?: string; page: number }) => {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'pv2', 'scaling'] });

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.pv2Scaling(filters),
    queryFn: () =>
      getScalingSettings({
        theme: filters.theme,
        sport: filters.sport,
        format: filters.format,
        page: filters.page,
        pageSize: PAGE_SIZE,
      }),
  });

  const createMutation = useMutation({
    mutationFn: (payload: ScalingCreatePayload) => createScalingSetting(payload),
    onSuccess: () => {
      invalidate();
      toaster.create({ title: 'Réglage créé', type: 'success' });
    },
    onError: (error) => toaster.create({ title: 'Erreur', description: errorMessage(error), type: 'error' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({
      formatId,
      themeId,
      sportIdentifier,
      payload,
    }: {
      formatId: string;
      themeId: string;
      sportIdentifier: string;
      payload: ScalingUpdatePayload;
    }) => updateScalingSetting(formatId, themeId, sportIdentifier, payload),
    onSuccess: () => {
      invalidate();
      toaster.create({ title: 'Réglage mis à jour', type: 'success' });
    },
    onError: (error) => toaster.create({ title: 'Erreur', description: errorMessage(error), type: 'error' }),
  });

  const deleteMutation = useMutation({
    mutationFn: ({
      formatId,
      themeId,
      sportIdentifier,
    }: {
      formatId: string;
      themeId: string;
      sportIdentifier: string;
    }) => deleteScalingSetting(formatId, themeId, sportIdentifier),
    onSuccess: () => {
      invalidate();
      toaster.create({ title: 'Réglage supprimé', type: 'success' });
    },
    onError: (error) => toaster.create({ title: 'Erreur', description: errorMessage(error), type: 'error' }),
  });

  return {
    items: data?.items ?? [],
    total: data?.total ?? 0,
    pageSize: PAGE_SIZE,
    isLoading,
    createScaling: createMutation.mutateAsync,
    updateScaling: updateMutation.mutateAsync,
    deleteScaling: deleteMutation.mutateAsync,
    isSaving: createMutation.isPending || updateMutation.isPending,
    invalidate,
  };
};
